import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import Omit from "lodash/omit";
import { Controller, type Control, FieldValues, Path } from "react-hook-form";
import Select, { components } from "react-select";

import CheckedIcon from "./assets/checked.svg?react";
import ChevronIcon from "./assets/chevron.svg?react";
import formStyles from "@/styles/form.module.scss";

interface PartFormMultiselectProps<
  TFieldValues extends FieldValues,
  OptionType extends object,
> {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  loading?: boolean;
  options: OptionType[];
  getOptionValue: (option: OptionType) => string;
  getOptionLabel: (option: OptionType) => string;
  maxWidth?: number;
  rules?: { [k: string]: unknown };
  disabled?: boolean;
  placeholder?: string;
  enableSelectAllOptionMenu?: boolean; // <== добавлено
}

const PartFormMultiselect = <TFieldValues extends FieldValues, OptionType extends object>(
  props: PartFormMultiselectProps<TFieldValues, OptionType>,
) => {
  const {
    name,
    control,
    loading,
    options,
    getOptionValue: _getOptionValue,
    getOptionLabel: _getOptionLabel,
    maxWidth,
    rules = { required: false },
    disabled,
    placeholder,
    enableSelectAllOptionMenu,
  } = props;

  // TODO Refactor to remove maxWidth

  const ALL_OPTION_VALUE = "__ALL__";

  const allOption = {
    [ALL_OPTION_VALUE]: ALL_OPTION_VALUE,
  } as unknown as OptionType;

  const isAllSelected = (value: string[]) => value?.length === options.length;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { value, onChange, ref }, fieldState: { error } }) => {
        const extendedOptions = enableSelectAllOptionMenu
          ? [allOption, ...options]
          : options;

        const getOptionValue = (option: OptionType) => {
          if (option === allOption) return ALL_OPTION_VALUE;
          return _getOptionValue(option);
        };

        const getOptionLabel = (option: OptionType) => {
          if (option === allOption && isAllSelected(value)) {
            return "Unselect All";
          }
          if (option === allOption && !isAllSelected(value)) {
            return "Select All";
          }
          return _getOptionLabel(option);
        };

        const DropdownIndicator = ({ disabled }: { disabled?: boolean }) => {
          if (disabled) return null;
          return <ChevronIcon />;
        };

        const Option = (args: any) => {
          let isChecked = false;
          if (args.data !== allOption && value.includes(getOptionValue(args.data))) {
            isChecked = true;
          }

          return (
            <components.Option {...args}>
              <span>{args.children}</span>
              {isChecked ? <CheckedIcon className={formStyles.selectedIcon} /> : null}
            </components.Option>
          );
        };

        const components1 = {
          IndicatorSeparator: () => null,
          DropdownIndicator: () => <DropdownIndicator disabled={disabled} />,
          Option,
          ...(disabled ? { MultiValueRemove: () => null } : {}),
        };

        return (
          <Select<OptionType, true>
            options={extendedOptions}
            value={options.filter(option => value?.includes(getOptionValue(option)))}
            onChange={selectedOptions => {
              const selectedValues = selectedOptions.map(getOptionValue);
              console.log("CALL onChange");
              if (
                enableSelectAllOptionMenu &&
                selectedOptions.includes(allOption) &&
                !isAllSelected(value)
              ) {
                return onChange(options.map(option => getOptionValue(option))); // выбрать всё
              }

              if (
                enableSelectAllOptionMenu &&
                selectedOptions.includes(allOption) &&
                isAllSelected(value)
              ) {
                return onChange([]); // снять всё
              }

              onChange(selectedValues);
            }}
            isDisabled={disabled}
            isLoading={loading}
            isClearable={false}
            backspaceRemovesValue={false}
            hideSelectedOptions={false}
            placeholder={placeholder || "All..."}
            getOptionValue={option => getOptionValue(option)}
            getOptionLabel={option => getOptionLabel(option)}
            classNamePrefix="RS"
            isMulti
            closeMenuOnSelect={false}
            classNames={{
              control: () =>
                cx(formStyles.formMultiSelect, { [formStyles.invalid]: !isEmpty(error) }),
              menu: () => formStyles.formMultiSelectOptionsList,
              option: () => formStyles.formMultiSelectOptionsListItem,
              valueContainer: () => formStyles.formMultiSelectValueContainer,
            }}
            styles={{
              container: base => ({ ...base, maxWidth }),
              option: () => ({}),
              menu: base => ({
                ...Omit(base, ["boxShadow", "border", "borderRadius"]),
                marginTop: 1,
              }),
              control: () => ({}),
              valueContainer: base => ({
                ...Omit(base, ["padding"]),
              }),
            }}
            components={components1}
            ref={ref}
          />
        );
      }}
    />
  );
};

export default PartFormMultiselect;
